import { useCallback, useEffect, useState } from 'react'
import type { FormEvent } from 'react'

import { ApiError } from '../../lib/api'
import { canEdit } from '../../lib/nav'
import { DatePicker } from '../../lib/DatePicker'
import { Pager } from '../../lib/Pager'
import { SortBar, SortTh, useSort } from '../../lib/Sort'
import { formatDateRange } from '../../lib/datetime'
import { MODE_LABEL, errorText, useOffsite } from '../../lib/offsite'
import type {
  OffsiteEmployee,
  OffsiteMode,
  OffsitePermission,
  OffsitePermissionSite,
  OffsiteSite,
  PermissionPayload,
} from '../../lib/offsite'
import { useAuthedSession } from '../../lib/SessionContext'
import { ReasonPrompt, SectionHead, ViewerNote } from './parts'

type StatusFilter = 'active' | 'all'

const NO_SITE_WARNING = 'ยังไม่ระบุสถานที่ — คนนี้ลงเวลาไม่ได้เลย'

/**
 * สรุปสถานที่ของใบเป็นข้อความบรรทัดเดียว (ใช้ในกล่องเทียบใบเดิมตอนชน 409)
 *
 * ⚠️ ไม่มีเคส "ทุกสถานที่" อีกแล้ว — head_office ที่ไม่มีสถานที่เลยคือ**ตั้งค่าค้าง**
 *    ไม่ใช่ "ได้ทุกที่" ต้องอ่านแล้วรู้ทันทีว่าใบนั้นใช้ไม่ได้
 */
function siteText(permission: OffsitePermission): string {
  if (permission.mode === 'anywhere') return 'ไม่ผูกสถานที่ (ไม่ตรวจพิกัด)'
  if (permission.sites.length === 0) return NO_SITE_WARNING
  return permission.sites.map(siteLabel).join(', ')
}

function siteLabel(site: OffsitePermissionSite): string {
  return `${site.code} · ${site.name}${disabledSuffix(site.status)}`
}

/** สถานที่ที่ถูกปิดหลังออกใบต้องมีป้ายกำกับทุกที่ที่โผล่ ไม่ใช่ดูเหมือนสถานที่ปกติ */
function disabledSuffix(status: 'active' | 'disabled'): string {
  return status === 'disabled' ? ' (ปิดใช้งานแล้ว)' : ''
}

export default function OffsitePermissions() {
  const session = useAuthedSession()
  const offsite = useOffsite()
  const writable = canEdit(session)

  const [status, setStatus] = useState<StatusFilter>('active')
  const [term, setTerm] = useState('')
  /** คำค้นที่ยิงไปแล้วจริง — แยกจากค่าในช่อง เพื่อไม่ให้ยิง API ทุกตัวอักษร */
  const [applied, setApplied] = useState('')

  const [permissions, setPermissions] = useState<OffsitePermission[] | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  // เปลี่ยนการเรียงต้องกลับหน้า 1 — ไม่งั้นค้างหน้า 8 ของลำดับเดิมแล้วเห็นตารางว่าง
  const sort = useSort([{ key: 'status', dir: 'asc' }, { key: 'empCode', dir: 'asc' }], () => setPage(1))
  const [sites, setSites] = useState<OffsiteSite[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  /** ใบที่กำลังแก้ — มีได้เฉพาะใบที่ยัง active (ปุ่มแก้ไขไม่โผล่ในแถวที่ยกเลิกแล้ว) */
  const [editing, setEditing] = useState<OffsitePermission | null>(null)
  const [revoking, setRevoking] = useState<OffsitePermission | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await offsite.listPermissions({ status, q: applied, page, pageSize, sort: sort.param })
      setPermissions(res.permissions)
      setTotal(res.total)
      setLoadError(null)
    } catch (err) {
      setLoadError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [offsite, status, applied, page, pageSize, sort.param])

  useEffect(() => {
    void load()
  }, [load])

  // รายการสถานที่โหลดครั้งเดียว — ใช้เป็นตัวเลือกในฟอร์มเท่านั้น
  // (ตารางไม่ต้องพึ่งรายการนี้แล้ว เพราะ /permissions ส่ง sites มาพร้อมชื่อในตัว)
  useEffect(() => {
    let alive = true
    offsite
      .listSites()
      .then((rows) => {
        if (alive) setSites(rows)
      })
      .catch(() => {
        // โหลดสถานที่ไม่ได้ไม่ควรทำให้ตารางใบอนุญาตหายไปทั้งหน้า
        // ฟอร์มจะไม่มีตัวเลือกให้ติ๊ก (แล้วกดบันทึกไม่ได้) ซึ่งปลอดภัยกว่าเดา
      })
    return () => {
      alive = false
    }
  }, [offsite])

  function applySearch(event: FormEvent) {
    event.preventDefault()
    setApplied(term.trim())
    setPage(1)
  }

  return (
    <>
      <h1>สิทธิ์ลงเวลานอกสถานที่</h1>
      <p className="meta">
        ใบอนุญาตให้ลงเวลาผ่าน LINE รายคน · หนึ่งคนมีใบที่ใช้งานอยู่ได้ใบเดียว ·
        ใบที่ใช้งานอยู่แก้ไขได้ (เปลี่ยนตัวคนไม่ได้) ส่วน<strong>ใบที่ยกเลิกแล้วแก้ไม่ได้
        ต้องออกใบใหม่</strong> เพราะเป็นประวัติที่รายงานย้อนหลังอ้างถึง
      </p>
      <p className="meta">
        หนึ่งใบผูกได้หลายสถานที่ และผูก<strong>เฉพาะที่ระบุไว้เท่านั้น</strong> —
        เปิดสาขาใหม่แล้วจะยังไม่มีใครลงเวลาที่นั่นได้ จนกว่าจะกลับมาเพิ่มสาขานั้นเข้าไปในใบ
      </p>

      {!writable && <ViewerNote />}
      {loadError && <p className="error">โหลดรายการไม่สำเร็จ — {loadError}</p>}
      {notice && <p className="ok">{notice}</p>}

      {writable && !showForm && (
        <p>
          <button type="button" onClick={() => setShowForm(true)}>
            ออกใบอนุญาตใหม่
          </button>
        </p>
      )}

      {writable && showForm && (
        <IssueForm
          sites={sites}
          onIssued={(message) => {
            setShowForm(false)
            setNotice(message)
            void load()
          }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {writable && editing && (
        <EditForm
          permission={editing}
          sites={sites}
          onSaved={(message) => {
            setEditing(null)
            setNotice(message)
            void load()
          }}
          onCancel={() => {
            setEditing(null)
            // ใบอาจถูกยกเลิกไปแล้วระหว่างที่ฟอร์มเปิดค้าง — โหลดใหม่ให้ตารางตรงกับของจริง
            void load()
          }}
        />
      )}

      {revoking && (
        <ReasonPrompt
          title={`ยกเลิกใบอนุญาตของ ${revoking.empName ?? revoking.empCode}`}
          detail={`รหัส ${revoking.empCode} · ${MODE_LABEL[revoking.mode]} · ยกเลิกแล้วพนักงานจะลงเวลาผ่าน LINE ไม่ได้ทันที`}
          confirmLabel="ยกเลิกใบอนุญาต"
          placeholder="เช่น ย้ายกลับมาทำงานที่ออฟฟิศแล้ว"
          onConfirm={async (reason) => {
            await offsite.revokePermission(revoking.id, reason)
            setRevoking(null)
            setNotice(`ยกเลิกใบอนุญาตของ ${revoking.empCode} แล้ว`)
            await load()
          }}
          onClose={() => setRevoking(null)}
        />
      )}

      <section className="card">
        <SectionHead
          title="รายการใบอนุญาต"
          meta={permissions ? `พบ ${permissions.length} รายการ` : undefined}
          onReload={() => void load()}
          loading={loading}
        />

        <form className="toolbar" onSubmit={applySearch}>
          <label>
            สถานะ
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as StatusFilter)
                setPage(1)
              }}
            >
              <option value="active">ใช้งานอยู่</option>
              <option value="all">ทั้งหมด (รวมที่ยกเลิกแล้ว)</option>
            </select>
          </label>
          <label>
            ค้นหา
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="รหัสพนักงาน หรือชื่อ"
            />
          </label>
          <button type="submit" disabled={loading}>
            ค้นหา
          </button>
          {applied !== '' && (
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setTerm('')
                setApplied('')
              }}
            >
              ล้างคำค้น
            </button>
          )}
        </form>

        {permissions === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : permissions.length === 0 ? (
          <p className="meta">
            ไม่พบใบอนุญาตตามเงื่อนไขนี้
            {status === 'active' && ' — ถ้ายังไม่เคยออกใบให้ใคร จะไม่มีใครลงเวลาผ่าน LINE ได้เลย'}
          </p>
        ) : (
          <>
            <SortBar
              sort={sort.sort}
              labels={{ empCode: 'พนักงาน', mode: 'โหมด', effectiveFrom: 'ช่วงวันที่', status: 'สถานะ' }}
              onClear={() => sort.setSort([])}
            />
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <SortTh label="พนักงาน" sortKey="empCode" sort={sort.sort} onToggle={sort.toggle} />
                    <SortTh label="โหมด" sortKey="mode" sort={sort.sort} onToggle={sort.toggle} />
                    {/* สถานที่เป็นหลายค่าต่อใบ (แตกมาจาก subquery) เรียงไม่ได้ */}
                    <th>สถานที่</th>
                    <SortTh label="ช่วงวันที่" sortKey="effectiveFrom" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                    <th>ผู้ออกให้</th>
                    <th>เหตุผล</th>
                    <SortTh label="สถานะ" sortKey="status" sort={sort.sort} onToggle={sort.toggle} />
                    {writable && <th>จัดการ</th>}
                  </tr>
                </thead>
                <tbody>
                  {permissions.map((p) => {
                    const revoked = p.status !== 'active'
                    return (
                      <tr key={p.id} className={revoked ? 'off' : undefined}>
                        <td>
                          {p.empName ?? '-'}
                          <div className="meta">
                            <code>{p.empCode}</code>
                          </div>
                        </td>
                        <td className="nowrap">
                          {MODE_LABEL[p.mode]}
                          {p.mode === 'anywhere' && <div className="meta">ไม่ตรวจพิกัดเลย</div>}
                        </td>
                        <td>
                          <PermissionSitesCell permission={p} />
                        </td>
                        <td className="nowrap">{formatDateRange(p.effectiveFrom, p.effectiveTo)}</td>
                        {/* ⚠️ ตั้งใจไม่โชว์ createdAt/revokedAt: สองคอลัมน์นั้นเก็บเป็น **UTC**
                            (ต่างจาก effective_from/to ที่เป็นเวลาไทย ดูหัวไฟล์ SQL) และสัญญา API
                            ยังไม่ได้บอกว่าส่งกลับมาแบบติด Z หรือไม่ → แปลงผิดทางไหนก็เพี้ยน 7 ชั่วโมง
                            เวลาผิดที่ดูเหมือนถูกแย่กว่าไม่โชว์เวลาเลย */}
                        <td className="nowrap">{p.createdBy ?? '-'}</td>
                        <td>{p.note ?? '-'}</td>
                        <td>
                          <span className={revoked ? 'chip chip--off' : 'chip chip--ok'}>
                            {revoked ? 'ยกเลิกแล้ว' : 'ใช้งานอยู่'}
                          </span>
                          {revoked && p.revokeReason && <div className="meta">{p.revokeReason}</div>}
                        </td>
                        {writable && (
                          <td>
                            {revoked ? (
                              // ใบที่ยกเลิกแล้วคือประวัติ แก้ไม่ได้ (backend ก็ตอบ 409 อยู่แล้ว)
                              // บอกทางออกไว้ตรงนี้เลย ไม่ให้ผู้ใช้ต้องเดาว่าทำไมไม่มีปุ่ม
                              <span className="meta">ยกเลิกแล้ว — ออกใบใหม่แทน</span>
                            ) : (
                              <div className="row-actions">
                                <button
                                  type="button"
                                  className="ghost tiny"
                                  onClick={() => {
                                    setEditing(p)
                                    setNotice(null)
                                  }}
                                >
                                  แก้ไข
                                </button>
                                <button
                                  type="button"
                                  className="ghost tiny"
                                  onClick={() => setRevoking(p)}
                                >
                                  ยกเลิกใบ
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        <Pager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          onPage={setPage}
          unit="ใบ"
          onPageSize={(n) => {
            setPageSize(n)
            setPage(1)
          }}
        />
      </section>
    </>
  )
}

/* ─────────────────────────── ช่องสถานที่ในตาราง ─────────────────────────── */

/**
 * แสดงสถานที่ทั้งหมดที่ใบนี้ผูกไว้
 *
 * ⚠️ สามเคสนี้ต้องแยกกันให้ออกด้วยตา ห้ามยุบรวม:
 *    - anywhere        = ตั้งใจไม่ผูกสถานที่ (ปกติ)
 *    - head_office + [] = ตั้งค่าค้าง ลงเวลาไม่ได้เลย (ต้องสะดุดตา)
 *    - มีสถานที่ที่ปิดใช้งานปนอยู่ = สิทธิ์ส่วนนั้นใช้ไม่ได้แล้ว แต่ยังอยู่ในใบ
 */
function PermissionSitesCell({ permission }: { permission: OffsitePermission }) {
  if (permission.mode === 'anywhere') {
    return <span className="meta">ไม่ผูกสถานที่</span>
  }

  if (permission.sites.length === 0) {
    return <span className="chip chip--warn">{NO_SITE_WARNING}</span>
  }

  return (
    <>
      {/* ไม่ใส่ .nowrap — ใบหนึ่งผูกได้หลายสาขา ถ้าห้ามตัดบรรทัดคอลัมน์นี้จะดันตารางกว้างเกินจอ */}
      <div>
        {permission.sites.map((s, i) => (
          <span key={s.id}>
            {i > 0 && ', '}
            <span className={s.status === 'disabled' ? 'bad' : undefined}>{s.code}</span>
          </span>
        ))}
      </div>
      <div className="meta">
        {permission.sites.map((s) => s.name + disabledSuffix(s.status)).join(' · ')}
      </div>
    </>
  )
}

/* ─────────────────────────── ฟอร์มออกใบใหม่ ─────────────────────────── */

function IssueForm({
  sites,
  onIssued,
  onCancel,
}: {
  sites: OffsiteSite[]
  onIssued: (message: string) => void
  onCancel: () => void
}) {
  const offsite = useOffsite()

  const [employee, setEmployee] = useState<OffsiteEmployee | null>(null)
  const [mode, setMode] = useState<OffsiteMode>('head_office')
  const [siteIds, setSiteIds] = useState<number[]>([])
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [note, setNote] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** ใบเดิมที่ backend บอกว่าชนอยู่ (409) — ต้องให้ผู้ใช้เห็นก่อนถึงจะทับได้ */
  const [conflict, setConflict] = useState<{
    message: string
    existing: OffsitePermission | null
  } | null>(null)

  const rangeBad = from !== '' && to !== '' && to < from
  // head_office ที่ไม่มีสถานที่เลย backend ตอบ 400 — กันไว้ตั้งแต่ปุ่มเพื่อไม่ให้โดนตีกลับ
  const sitesMissing = mode === 'head_office' && siteIds.length === 0
  const canSubmit = !submitting && employee !== null && !rangeBad && !sitesMissing

  function payloadOf(replace: boolean): PermissionPayload {
    const body: PermissionPayload = {
      empCode: employee?.empCode ?? '',
      mode,
      // anywhere ต้องส่ง [] เท่านั้น (ส่งไม่ว่าง = 400) — ไม่ล้าง state ทิ้ง
      // เผื่อผู้ใช้สลับโหมดไปกลับ จะได้ไม่ต้องติ๊กใหม่ทั้งหมด
      siteIds: mode === 'head_office' ? siteIds : [],
      // ⚠️ DatePicker คืนค่าเป็น YYYY-MM-DD ที่ผ่าน checkDate แล้ว และคอลัมน์เป็น DATE
      //    ส่งข้อความนี้ตรง ๆ ห้ามแปลงผ่าน new Date() (จะกลายเป็น UTC แล้ววันเลื่อน)
      effectiveFrom: from === '' ? null : from,
      effectiveTo: to === '' ? null : to,
      note: note.trim() === '' ? null : note.trim(),
    }
    return replace ? { ...body, replace: true } : body
  }

  async function issue(replace: boolean) {
    if (!employee) return
    setSubmitting(true)
    setError(null)
    try {
      await offsite.createPermission(payloadOf(replace))
      onIssued(
        replace
          ? `แทนที่ใบอนุญาตของ ${employee.empCode} ด้วยใบใหม่แล้ว`
          : `ออกใบอนุญาตให้ ${employee.empCode} แล้ว`,
      )
    } catch (err) {
      // 409 = คนนี้มีใบที่ใช้งานอยู่แล้ว (unique index UX_lcp_active_emp)
      // ต้องเอาใบเดิมมาให้ดูก่อน แล้วให้ผู้ใช้ยืนยันว่าจะทับ — replace ส่งรอบสองเท่านั้น
      if (err instanceof ApiError && err.status === 409 && !replace) {
        // สัญญา API ไม่ได้ระบุว่า 409 ส่งใบเดิมกลับมาด้วย จึงไปดึงมาเองด้วยรหัสพนักงาน
        const existing = await offsite
          // กรองด้วย empCode อยู่แล้ว ใบที่ใช้งานอยู่มีได้ใบเดียว → อยู่ในหน้าแรกแน่นอน
          .listPermissions({ status: 'active', q: employee.empCode, pageSize: 5 })
          .then((res) => res.permissions.find((r) => r.empCode === employee.empCode) ?? null)
          .catch(() => null)
        setConflict({ message: err.message, existing })
      } else {
        setError(errorText(err))
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="card form-card--wide">
      <h2>ออกใบอนุญาตใหม่</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          // ตอนกล่อง 409 ค้างอยู่ ปุ่ม submit ถูกซ่อนไว้ แต่ Enter ในช่อง input ยัง submit ได้
          // ถ้าไม่กันไว้จะยิงใบใหม่แบบไม่ replace ซ้ำอีกรอบแล้วชน 409 เหมือนเดิม
          if (canSubmit && !conflict) void issue(false)
        }}
      >
        {employee === null ? (
          <EmployeePicker onPick={setEmployee} />
        ) : (
          <div>
            <div className="row-actions">
              <strong>{employee.empName ?? employee.empCode}</strong>
              <code>{employee.empCode}</code>
              <button
                type="button"
                className="ghost tiny"
                onClick={() => {
                  setEmployee(null)
                  setConflict(null)
                }}
              >
                เปลี่ยนคน
              </button>
            </div>
            <p className="meta">
              {[employee.department, employee.position].filter(Boolean).join(' · ') || 'ไม่มีข้อมูลแผนก'}
            </p>
            {employee.hasPermission && (
              <p className="warn">
                คนนี้มีใบอนุญาตที่ใช้งานอยู่แล้ว — ออกใบใหม่จะต้องยืนยันแทนที่ใบเดิมอีกครั้ง
              </p>
            )}
          </div>
        )}

        <PermissionFields
          sites={sites}
          mode={mode}
          setMode={setMode}
          siteIds={siteIds}
          setSiteIds={setSiteIds}
          from={from}
          setFrom={setFrom}
          to={to}
          setTo={setTo}
          note={note}
          setNote={setNote}
          rangeBad={rangeBad}
          sitesMissing={sitesMissing}
        />

        {error && <p className="error">{error}</p>}

        {conflict && (
          <div className="warn">
            <strong>{conflict.message || 'พนักงานคนนี้มีใบอนุญาตที่ใช้งานอยู่แล้ว'}</strong>
            {conflict.existing ? (
              <dl>
                <dt>โหมดเดิม</dt>
                <dd>{MODE_LABEL[conflict.existing.mode]}</dd>
                <dt>สถานที่เดิม</dt>
                <dd>{siteText(conflict.existing)}</dd>
                <dt>ช่วงวันที่เดิม</dt>
                <dd>{formatDateRange(conflict.existing.effectiveFrom, conflict.existing.effectiveTo)}</dd>
                <dt>เหตุผลเดิม</dt>
                <dd>{conflict.existing.note ?? '-'}</dd>
                <dt>ผู้ออกให้</dt>
                <dd>{conflict.existing.createdBy ?? '-'}</dd>
              </dl>
            ) : (
              <p>ดึงรายละเอียดใบเดิมมาแสดงไม่ได้ — ดูได้ที่ตารางด้านล่างโดยค้นจากรหัสพนักงาน</p>
            )}
            <p>ยืนยันแทนที่ = ยกเลิกใบเดิมแล้วออกใบใหม่ตามที่กรอกไว้ข้างบน (ใบเดิมยังอยู่ในประวัติ)</p>
            <div className="row-actions">
              {/* กล่องนี้ค้างอยู่ได้ขณะที่ช่องข้างบนยังแก้ได้ — ถ้าผู้ใช้เอาติ๊กสถานที่ออกหมด
                  ระหว่างนี้แล้วกดแทนที่ จะโดน 400 กลับมา จึงใช้เงื่อนไขเดียวกับปุ่ม submit */}
              <button type="button" onClick={() => void issue(true)} disabled={!canSubmit}>
                {submitting ? 'กำลังบันทึก…' : 'ยืนยันแทนที่ใบเดิม'}
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => setConflict(null)}
                disabled={submitting}
              >
                ไม่แทนที่
              </button>
            </div>
          </div>
        )}

        {!conflict && (
          <div className="row-actions">
            <button type="submit" disabled={!canSubmit}>
              {submitting ? 'กำลังบันทึก…' : 'ออกใบอนุญาต'}
            </button>
            <button type="button" className="ghost" onClick={onCancel} disabled={submitting}>
              ยกเลิก
            </button>
          </div>
        )}
      </form>
    </section>
  )
}

/* ──────────── ช่องกรอกที่ใช้ร่วมกันระหว่าง "ออกใบใหม่" กับ "แก้ใบเดิม" ────────────
 * อยู่ที่เดียวเพราะกฎการเล่นระหว่างโหมดกับสถานที่ต้องเหมือนกันทั้งสองฟอร์ม
 * ถ้าแยกกันเขียน วันหนึ่งฟอร์มแก้ไขจะไม่มีคำเตือน "anywhere = ไม่ตรวจพิกัด" แล้วไม่มีใครรู้
 */
function PermissionFields({
  sites,
  boundSites,
  mode,
  setMode,
  siteIds,
  setSiteIds,
  from,
  setFrom,
  to,
  setTo,
  note,
  setNote,
  rangeBad,
  sitesMissing,
}: {
  sites: OffsiteSite[]
  /** สถานที่ที่ใบนี้ผูกไว้ตอนเปิดฟอร์ม — มีเฉพาะตอนแก้ใบเดิม (ดู SiteChoices) */
  boundSites?: OffsitePermissionSite[]
  mode: OffsiteMode
  setMode: (v: OffsiteMode) => void
  siteIds: number[]
  setSiteIds: (v: number[]) => void
  from: string
  setFrom: (v: string) => void
  to: string
  setTo: (v: string) => void
  note: string
  setNote: (v: string) => void
  rangeBad: boolean
  sitesMissing: boolean
}) {
  return (
    <>
      <div className="form-row">
        <label>
          โหมด
          <select value={mode} onChange={(e) => setMode(e.target.value as OffsiteMode)}>
            <option value="head_office">{MODE_LABEL.head_office} (ต้องอยู่ในรัศมี)</option>
            <option value="anywhere">{MODE_LABEL.anywhere} (ไม่ตรวจพิกัด)</option>
          </select>
        </label>
      </div>

      {mode === 'head_office' && (
        <SiteChoices
          sites={sites}
          boundSites={boundSites}
          siteIds={siteIds}
          setSiteIds={setSiteIds}
          sitesMissing={sitesMissing}
        />
      )}

      {mode === 'anywhere' && (
        <p className="warn">
          โหมดนี้ลงเวลาได้จากทุกที่ รวมทั้งจากบ้าน — <strong>ระบบไม่ตรวจพิกัดเลย</strong> ควรใช้เท่าที่จำเป็น
          และกำหนดวันสิ้นสุดไว้ด้วย
        </p>
      )}

      <div className="form-row">
        <label>
          เริ่มมีผล (ไม่กรอก = ทันที)
          <DatePicker value={from} onChange={setFrom} max={to === '' ? undefined : to} />
        </label>
        <label>
          สิ้นสุด (ไม่กรอก = ไม่จำกัด)
          {/* min = วันเริ่ม → ปฏิทินปิดวันที่เลือกไม่ได้ไว้เลย ไม่ต้องรอ error หลังกรอก */}
          <DatePicker value={to} onChange={setTo} min={from === '' ? undefined : from} />
          {rangeBad && <small className="bad">วันสิ้นสุดต้องไม่ก่อนวันเริ่ม</small>}
        </label>
      </div>

      {mode === 'anywhere' && to === '' && (
        <small className="hint">ไม่กรอกวันสิ้นสุด = ให้สิทธิ์ลงเวลาจากที่ใดก็ได้แบบไม่มีกำหนด</small>
      )}

      <label>
        เหตุผลที่ให้สิทธิ์
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={255}
          placeholder="เช่น ประจำไซต์งานลูกค้า ก.ค.–ก.ย."
        />
        <small className="hint">ควรกรอกไว้ — เวลาตรวจสอบย้อนหลังจะได้รู้ว่าใบนี้มาจากเรื่องอะไร</small>
      </label>
    </>
  )
}

/* ──────────────────────── เลือกสถานที่ (หลายแห่ง) ──────────────────────── */

/** แถวหนึ่งใน checkbox list — รวมสถานที่ที่เลือกใหม่ได้กับที่ปิดใช้งานไปแล้วไว้ด้วยกัน */
type SiteChoice = {
  id: number
  code: string
  name: string
  /** null = ไม่รู้รัศมี (สถานที่ที่ถูกปิดไปแล้ว หรือโหลดรายการสถานที่ไม่สำเร็จ) */
  radiusM: number | null
  disabled: boolean
}

/**
 * ⛔ ที่นี่ **ห้ามมีตัวเลือก "ทุกสถานที่"** ไม่ว่าจะเป็น option, checkbox 'all'
 *    หรือ "ไม่ติ๊กอะไรเลย = ได้ทุกที่" — นั่นคือช่องโหว่ที่ 006_permission_sites.sql
 *    ตั้งใจปิด (เปิดสาขาใหม่แล้วทุกคนได้สิทธิ์ทันทีโดยไม่มีใครอนุมัติ)
 *    "เลือกทั้งหมด" ที่นี่ = ติ๊กสถานที่ที่มีอยู่ ณ ตอนนี้ทีละอัน ซึ่งเป็นรายการจริง
 *    ไม่ขยายตามสาขาที่เพิ่มทีหลัง
 */
function SiteChoices({
  sites,
  boundSites,
  siteIds,
  setSiteIds,
  sitesMissing,
}: {
  sites: OffsiteSite[]
  boundSites?: OffsitePermissionSite[]
  siteIds: number[]
  setSiteIds: (v: number[]) => void
  sitesMissing: boolean
}) {
  const activeSites = sites.filter((s) => s.status === 'active')
  const activeIds = new Set(activeSites.map((s) => s.id))

  /**
   * สถานที่ที่ใบเดิมผูกไว้แต่ถูกปิดใช้งานไปแล้ว — ต้องโชว์ต่อ ไม่ใช่หายเงียบ ๆ
   * ไม่งั้นคนเปิดฟอร์มมาแก้เรื่องอื่นแล้วกดบันทึก สิทธิ์นั้นจะหลุดไปโดยไม่ตั้งใจ
   *
   * ⚠️ อ่านจาก boundSites (ค่าตอนเปิดฟอร์ม) ไม่ใช่ siteIds ปัจจุบัน — ถ้าอ่านจาก siteIds
   *    พอผู้ใช้เอาติ๊กออก แถวจะหายไปทันทีจนติ๊กกลับไม่ได้
   */
  const closedChoices: SiteChoice[] = (boundSites ?? [])
    .filter((b) => !activeIds.has(b.id))
    .map((b) => {
      // ชื่อล่าสุดอยู่ใน listSites() (อาจถูกแก้หลังออกใบ) — ใช้ของ boundSites เป็นตัวสำรอง
      const known = sites.find((s) => s.id === b.id)
      return {
        id: b.id,
        code: known?.code ?? b.code,
        name: known?.name ?? b.name,
        radiusM: known?.radiusM ?? null,
        disabled: true,
      }
    })

  const choices: SiteChoice[] = [
    ...activeSites.map((s) => ({
      id: s.id,
      code: s.code,
      name: s.name,
      radiusM: s.radiusM,
      disabled: false,
    })),
    ...closedChoices,
  ]

  function toggle(id: number, on: boolean) {
    // เรียง id ไว้เสมอ เพื่อให้ payload ของการติ๊กชุดเดียวกันออกมาเหมือนกันทุกครั้ง
    setSiteIds(on ? [...siteIds, id].sort((a, b) => a - b) : siteIds.filter((x) => x !== id))
  }

  /** ติ๊กสถานที่ที่เปิดใช้อยู่ทั้งหมด แต่ไม่ปลดที่ปิดใช้งานซึ่งใบเดิมผูกไว้ */
  function selectAll() {
    setSiteIds([...new Set([...siteIds, ...activeIds])].sort((a, b) => a - b))
  }

  return (
    <div>
      <strong>สถานที่ที่อนุญาต</strong>

      {choices.length === 0 ? (
        <p className="warn">
          ไม่มีสถานที่ให้เลือก — ต้องไปเพิ่ม (หรือเปิดใช้) สถานที่ที่หน้า “สถานที่ลงเวลา” ก่อน
          จึงจะออกใบโหมดนี้ได้ · ถ้ามั่นใจว่ามีสถานที่อยู่แล้ว แปลว่าโหลดรายการไม่สำเร็จ ให้โหลดหน้าใหม่
        </p>
      ) : (
        <>
          <div className="row-actions">
            <button type="button" className="ghost tiny" onClick={selectAll}>
              เลือกทั้งหมด
            </button>
            <button type="button" className="ghost tiny" onClick={() => setSiteIds([])}>
              ล้างทั้งหมด
            </button>
            <span className="meta">
              เลือกแล้ว {siteIds.length} จาก {choices.length} แห่ง
            </span>
          </div>

          <div>
            {choices.map((c) => (
              <label className="check" key={c.id}>
                <input
                  type="checkbox"
                  checked={siteIds.includes(c.id)}
                  onChange={(e) => toggle(c.id, e.target.checked)}
                />
                <span>
                  <code>{c.code}</code> {c.name}{' '}
                  <span className="meta">
                    {c.radiusM === null ? 'ไม่ทราบรัศมี' : `รัศมี ${c.radiusM} ม.`}
                  </span>{' '}
                  {c.disabled && <span className="chip chip--off">ปิดใช้งานแล้ว</span>}
                </span>
              </label>
            ))}
          </div>

          <p className="meta">
            สิทธิ์มีเฉพาะสถานที่ที่ติ๊กไว้เท่านั้น — <strong>สาขาที่เพิ่มทีหลังจะไม่ถูกเพิ่มให้อัตโนมัติ</strong>
            ต้องกลับมาแก้ใบนี้เอง
          </p>
        </>
      )}

      {sitesMissing && (
        <p className="bad">
          ต้องเลือกอย่างน้อย 1 สถานที่ถึงจะบันทึกได้ — ใบที่ไม่มีสถานที่เลย = ลงเวลาไม่ได้เลย
        </p>
      )}

      {closedChoices.length > 0 && (
        <p className="meta">
          สถานที่ที่ปิดใช้งานแล้วจะไม่มีให้เลือกในใบใหม่ แต่ใบนี้ผูกไว้ตั้งแต่ก่อนปิด จึงยังแสดงอยู่ —
          ถ้าเอาติ๊กออกแล้วบันทึก จะเพิ่มกลับไม่ได้จนกว่าจะเปิดใช้สถานที่นั้นอีกครั้ง
        </p>
      )}
    </div>
  )
}

/* ──────────────────────── แก้ใบที่ยังใช้งานอยู่ ──────────────────────── */

/**
 * แก้ได้เฉพาะใบที่ยัง active — ตัวคนเปลี่ยนไม่ได้ (นั่นคือใบของอีกคน ต้องออกใบใหม่)
 *
 * ⚠️ ใบที่ถูกยกเลิกไปแล้วเป็น "ประวัติ" แก้ย้อนหลังไม่ได้ ปุ่มแก้ไขจึงไม่โผล่ในแถวนั้น
 *    และถ้ามีคนยกเลิกใบนี้จากอีกแท็บระหว่างที่ฟอร์มเปิดค้างอยู่ backend จะตอบ 409
 *    ซึ่งจัดการไว้ข้างล่างแล้ว — ไม่ปล่อยให้ขึ้นเป็น error ดิบที่ผู้ใช้อ่านไม่รู้เรื่อง
 */
function EditForm({
  permission,
  sites,
  onSaved,
  onCancel,
}: {
  permission: OffsitePermission
  sites: OffsiteSite[]
  onSaved: (message: string) => void
  onCancel: () => void
}) {
  const offsite = useOffsite()

  const [mode, setMode] = useState<OffsiteMode>(permission.mode)
  // เริ่มด้วยสถานที่เดิมทั้งหมด (รวมที่ปิดใช้งานไปแล้ว) — บันทึกโดยไม่แตะ = ได้เท่าเดิม
  const [siteIds, setSiteIds] = useState<number[]>(() =>
    permission.sites.map((s) => s.id).sort((a, b) => a - b),
  )
  const [from, setFrom] = useState(permission.effectiveFrom ?? '')
  const [to, setTo] = useState(permission.effectiveTo ?? '')
  const [note, setNote] = useState(permission.note ?? '')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [gone, setGone] = useState<string | null>(null)

  const rangeBad = from !== '' && to !== '' && to < from
  const sitesMissing = mode === 'head_office' && siteIds.length === 0

  async function save() {
    setSubmitting(true)
    setError(null)
    try {
      await offsite.updatePermission(permission.id, {
        mode,
        // anywhere ต้องเป็น [] เท่านั้น (เหมือนตอนออกใบใหม่) — state เดิมยังเก็บไว้
        siteIds: mode === 'head_office' ? siteIds : [],
        // DatePicker ให้ YYYY-MM-DD มาแล้ว ส่งตรง ๆ ห้ามผ่าน new Date()
        effectiveFrom: from === '' ? null : from,
        effectiveTo: to === '' ? null : to,
        note: note.trim() === '' ? null : note.trim(),
      })
      onSaved(`แก้ใบอนุญาตของ ${permission.empCode} แล้ว`)
    } catch (err) {
      // 409 = ใบถูกยกเลิกไปแล้ว (คนอื่นกดจากอีกแท็บ) — แก้ต่อไม่ได้ ต้องออกใบใหม่
      if (err instanceof ApiError && err.status === 409) setGone(err.message)
      else setError(errorText(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="card form-card--wide">
      <h2>แก้ใบอนุญาต — {permission.empName ?? permission.empCode}</h2>
      <p className="meta">
        <code>{permission.empCode}</code> · ออกโดย {permission.createdBy ?? '-'} ·
        เปลี่ยนตัวพนักงานในใบเดิมไม่ได้ ถ้าจะให้คนอื่นต้องออกใบใหม่
      </p>

      {gone ? (
        <>
          <p className="warn">{gone}</p>
          <div className="row-actions">
            <button type="button" onClick={onCancel}>
              ปิด
            </button>
          </div>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!submitting && !rangeBad && !sitesMissing) void save()
          }}
        >
          <PermissionFields
            sites={sites}
            boundSites={permission.sites}
            mode={mode}
            setMode={setMode}
            siteIds={siteIds}
            setSiteIds={setSiteIds}
            from={from}
            setFrom={setFrom}
            to={to}
            setTo={setTo}
            note={note}
            setNote={setNote}
            rangeBad={rangeBad}
            sitesMissing={sitesMissing}
          />

          {error && <p className="error">{error}</p>}

          <div className="row-actions">
            <button type="submit" disabled={submitting || rangeBad || sitesMissing}>
              {submitting ? 'กำลังบันทึก…' : 'บันทึกการแก้ไข'}
            </button>
            <button type="button" className="ghost" onClick={onCancel} disabled={submitting}>
              ยกเลิก
            </button>
          </div>
        </form>
      )}
    </section>
  )
}

/* ────────────────────── ค้นหาพนักงานแล้วเลือกจากผลลัพธ์ ────────────────────── */

/**
 * ต้องเลือกจากผลค้นหาเท่านั้น ห้ามพิมพ์ empCode มือเปล่า
 * — พิมพ์ผิดหนึ่งตัวจะได้ใบอนุญาตของรหัสที่ไม่มีตัวตน แล้วไม่มีอะไรฟ้อง
 *   (ตาราง permission ไม่มี FK ไป Users เพราะเป็นตารางของ HR ที่เราแตะไม่ได้)
 */
function EmployeePicker({ onPick }: { onPick: (employee: OffsiteEmployee) => void }) {
  const offsite = useOffsite()
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<OffsiteEmployee[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const q = term.trim()
    if (q.length < 2) {
      setResults(null)
      setError(null)
      return
    }

    // หน่วงไว้ก่อนยิง เพื่อไม่ให้พิมพ์หนึ่งตัวยิงหนึ่งครั้ง (ค้นบน MSSQL ของ HR)
    let alive = true
    const timer = setTimeout(() => {
      setSearching(true)
      setError(null)
      offsite
        .searchEmployees(q)
        .then((rows) => {
          if (alive) setResults(rows)
        })
        .catch((err: unknown) => {
          if (alive) setError(errorText(err))
        })
        .finally(() => {
          if (alive) setSearching(false)
        })
    }, 350)

    return () => {
      alive = false
      clearTimeout(timer)
    }
  }, [term, offsite])

  return (
    <div>
      <label>
        ค้นหาพนักงาน
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="รหัสพนักงาน หรือชื่อ (อย่างน้อย 2 ตัวอักษร)"
          // ช่องนี้อยู่ในฟอร์มออกใบ — ถ้าไม่กัน Enter จะกลายเป็นการ submit ฟอร์ม
          // ทั้งที่ผู้ใช้แค่อยากค้นหา
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault()
          }}
          autoFocus
        />
        <small className="hint">เลือกจากผลค้นหาเท่านั้น — พิมพ์รหัสเองอาจได้คนที่ไม่มีตัวตน</small>
      </label>

      {error && <p className="error">ค้นหาไม่สำเร็จ — {error}</p>}
      {searching && <p className="meta">กำลังค้นหา…</p>}
      {results !== null && results.length === 0 && !searching && (
        <p className="meta">ไม่พบพนักงานที่ตรงกับคำค้นนี้</p>
      )}

      {results !== null && results.length > 0 && (
        <div className="pick-list">
          {results.map((employee) => (
            <button key={employee.empCode} type="button" onClick={() => onPick(employee)}>
              {/* div ไม่ใช่ span — ข้างในมี .meta ที่เป็น block (span ครอบ div เป็น HTML ที่ไม่ถูกต้อง) */}
              <div>
                <strong>{employee.empName ?? employee.empCode}</strong>{' '}
                <code>{employee.empCode}</code>
                <div className="meta">
                  {[employee.department, employee.position].filter(Boolean).join(' · ') || '-'}
                </div>
              </div>
              {employee.hasPermission && <span className="chip chip--warn">มีใบอยู่แล้ว</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
